import type { TPhrases } from '@semios/app-platform-i18n-core'
import { translate } from 'i18n/i18n'
import { createElement, type ComponentType } from 'react'
import { checkAuthorization } from 'utils/checkAuthorization'
import { PEST_CONFIGS, type TPestDashboardConfig } from './_utils/pestDashboardConfig'
import { hasPestDataAccess } from './_utils/useDashboardPropertyOptions'
import { hasIrrigationAccess } from './_utils/useWaterPropertyOptions'
import { PestDashboard } from './views/pests/PestDashboard'
import { WaterMonitoring } from './views/water/WaterMonitoring'
import { WaterUsage } from './views/water/WaterUsage'

export type TDashboard = {
  id: string
  // Display title + description, resolved at render time so they react to locale changes (rather than
  // being frozen at module load). Pest dashboards prefix the translated insect name with "Pest - ";
  // other families (water, …) return their own plain title.
  getTitle: () => string
  getDescription: () => string
  Component: ComponentType
  // Whether the user can access this dashboard given the ids of their (loaded) properties — the gate
  // for listing it on the landing page. Each family supplies its own permission check.
  isAccessible: (propertyIds: number[]) => boolean
}

const makePestComponent = (config: TPestDashboardConfig): ComponentType => {
  const Component = () => createElement(PestDashboard, { config })

  Component.displayName = `PestDashboard(${config.id})`

  return Component
}

// Pest dashboards are one family: every entry renders the same parameterized PestDashboard, bound to
// its per-pest config. Adding a pest = add a PEST_CONFIGS entry (no new component). The display label
// and access check live here so the landing page stays family-agnostic.
const pestDashboards: TDashboard[] = PEST_CONFIGS.map((config) => ({
  id: config.id,
  // config.name is the English insect name (the dbInsectName key), rendered via the translated
  // "Pest - {{pestName}}" label so the prefix + name both localize.
  getTitle: () =>
    translate.phrases.banyanApp('Pest - {{pestName}}', {
      pestName: translate.phrases.dbInsectName(config.name as TPhrases['dbInsectName']),
    }),
  getDescription: () => translate.phrases.banyanApp('Trap counts and degree day timing'),
  Component: makePestComponent(config),
  isAccessible: (propertyIds) => propertyIds.some((pid) => hasPestDataAccess(pid, config.insectId)),
}))

// Water dashboards: standalone views (not a config-driven family like pests). Listed when the user
// has irrigation access on any property.
const waterDashboards: TDashboard[] = [
  {
    id: 'water-usage',
    getTitle: () => translate.phrases.banyanApp('Water Usage'),
    getDescription: () => translate.phrases.banyanApp('Irrigation activity, flow, and water use by zone'),
    Component: WaterUsage,
    isAccessible: (propertyIds) => propertyIds.some((pid) => hasIrrigationAccess(pid)),
  },
  {
    id: 'water-monitoring',
    getTitle: () => translate.phrases.banyanApp('Water Monitoring'),
    getDescription: () => translate.phrases.banyanApp('Soil moisture and water status by zone'),
    Component: WaterMonitoring,
    // Soil-moisture-centric dashboard; listed when the user has soil-moisture or irrigation access.
    isAccessible: (propertyIds) =>
      propertyIds.some(
        (pid) =>
          checkAuthorization({ permission: 'VIEW_SOIL_MOISTURE_DETAILS', entity: pid }) ||
          hasIrrigationAccess(pid),
      ),
  },
]

export const DASHBOARDS: TDashboard[] = [...pestDashboards, ...waterDashboards]
